import type {LoginCopy} from './useAuth';
import type {GuestCheckoutContext} from 'requests';
import {translate as t} from 'i18n/translator';
import {formatCount} from 'utils';
import type {LoginOptions} from '@it-s-ai/auth';
import type {rootStore} from 'stores/rootStore';

export const resultLoginCopy = (kind: 'ai' | 'plagiarism'): LoginCopy => ({
    title: t(`login-${kind}-results-title`),
    subtitle: t(`login-${kind}-results-subtitle`),
});

export const documentLoginCopy = (maxMb: number): LoginCopy => ({
    title: t('login-document-title', {mb: maxMb}),
    subtitle: t('login-document-subtitle'),
});

const PLAN_NAMES = new Map([['plus', 'Plus'], ['premium', 'Premium'], ['pro', 'Pro'], ['enterprise', 'Enterprise']]);

/** Only a server-confirmed payment gets the payment-complete message. Old API
 * responses and unavailable checkout details keep a neutral account-access prompt. */
export const guestClaimLoginCopy = (context?: GuestCheckoutContext | null): LoginCopy => {
    const plan = context?.plan && PLAN_NAMES.get(context.plan);
    return {
        title: context?.trial
            ? t('login-trial-title')
            : plan ? t('login-purchase-plan-title', {plan}) : t('login-purchase-title'),
        subtitle: !context?.trial && context?.payment_confirmed
            ? t('login-purchase-paid-subtitle') : t('login-purchase-subtitle'),
        perks: [],
        footer: null,
    };
};

/** Keep the free Humanizer offer behind the same availability rule as the workspace.
 * Purchase claims deliberately have no free-plan marketing copy. */
export const withHumanizerPerk = (options: LoginOptions, root: Pick<typeof rootStore, 'user' | 'pricing'>): LoginOptions => {
    if (!root.user.humanizerAvailable || options.reason === 'purchase' || options.copy?.perks?.length === 0) return options;
    const free = root.pricing.PLANS_LIMITS.free;
    const humanizer = options.reason === 'humanizer';
    const perks = options.copy?.perks ?? [
        {label: t('login-perk-words', {count: formatCount(free.granted_words_simple_scan)})},
        {label: t('login-perk-deep-scans', {count: free.deep_scans})},
        {label: t('login-perk-chars', {chars: formatCount(free.words_per_generation_limit)})},
        {label: t('login-perk-files', {mb: free.document_upload_limit_mb})},
    ];
    return {...options, copy: {...options.copy, perks: [
        ...perks.map(perk => humanizer ? {...perk, isKey: false} : perk),
        {label: t('login-perk-humanizer', {count: formatCount(root.pricing.freeHumanizerWords)}), isKey: humanizer},
    ]}};
};
